// src/components/ClientSummaryStrip.jsx
import React, { memo, useEffect, useMemo, useState } from "react";
import {
  ArrowUpRight,
  CalendarDays,
  CircleDollarSign,
  AlertTriangle,
  FileText,
  PiggyBank,
} from "lucide-react";
import api from "../api/axios";

/**
 * Production-grade Client Summary Strip
 * - Self-fetch mode (pass clientId) OR controlled mode (pass kpis + statusCounts)
 * - KPIs: A/R balance • Overdue (count + $) • Upcoming Work (next work details) • Open Quotes (count + $)
 * - Donut: invoice status counts {paid, partial, unpaid, overdue}
 * - Robust response normalization for /invoices, /works, /quotes
 */

/* --------------------------------- utils --------------------------------- */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * ✅ No USD hardcode:
 * - If currency is missing/invalid, we fall back to plain number formatting (not currency).
 */
const isValidCurrencyCode = (c) =>
  typeof c === "string" && /^[A-Z]{3}$/.test(c);

const nf = (currency) => {
  const cur = isValidCurrencyCode(currency) ? currency : null;
  if (!cur) {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 0,
    });
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: cur,
    maximumFractionDigits: 0,
  });
};

const fmtMaybeDateLabel = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
};

const safeNum = (v) => (typeof v === "number" && isFinite(v) ? v : 0);
const toNumber = (v) => (v == null ? 0 : Number(v) || 0);

const CLOSED_STATUSES = new Set([
  "done",
  "completed",
  "cancelled",
  "no_show",
  "void",
  "invoiced",
]);

const typeLabel = (t) => {
  const v = String(t || "").toLowerCase();
  if (v === "job") return "Job";
  if (v === "task") return "Task";
  if (v === "appointment" || v === "appt") return "Appointment";
  return "Work";
};

const primaryWhen = (w) =>
  w?.schedule?.start || w?.schedule?.end || w?.dueDate || null;

const fmtRange = (start, end) => {
  if (!start && !end) return "—";
  const s = start ? new Date(start) : null;
  const e = end ? new Date(end) : null;
  if (s && e) {
    const sameDay =
      s.getFullYear() === e.getFullYear() &&
      s.getMonth() === e.getMonth() &&
      s.getDate() === e.getDate();
    if (sameDay) {
      return `${s.toLocaleDateString()}, ${s.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })} – ${e.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })}`;
    }
    return `${s.toLocaleDateString()}, ${s.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    })} – ${e.toLocaleDateString()}, ${e.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  }
  if (s) {
    return `${s.toLocaleDateString()}, ${s.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  }
  return `${new Date(end).toLocaleDateString()}, ${new Date(
    end
  ).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
};

/* ------------------------------- primitives ------------------------------ */

const KpiCard = memo(function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  minH,
  onClick,
  "aria-label": ariaLabel,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel || label}
      className="group relative flex h-full w-full flex-col justify-center rounded-2xl border border-slate-200 bg-white px-5 py-4 text-left shadow-sm ring-1 ring-transparent transition hover:shadow-md hover:ring-blue-100"
      style={{ height: minH }}
    >
      <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition group-hover:opacity-100">
        <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-transparent [background:linear-gradient(135deg,rgba(99,102,241,0.08),rgba(14,165,233,0.08))]" />
      </div>

      <div className="flex items-start gap-3">
        {Icon ? (
          <div className="mt-0.5 rounded-xl bg-blue-50 p-1.5 text-blue-600">
            <Icon size={16} />
          </div>
        ) : null}
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {label}
          </div>
          <div className="mt-1 text-[22px] font-bold leading-6 text-slate-900 truncate">
            {value}
          </div>
          {sub ? (
            <div className="mt-0.5 text-[11px] font-medium text-slate-500 group-hover:text-slate-700">
              {sub}
            </div>
          ) : null}
        </div>
      </div>

      <ArrowUpRight
        size={14}
        className="absolute right-3 top-3 text-slate-300 transition group-hover:text-slate-400"
      />
    </button>
  );
});

function MiniDonut({ data, size = 120, stroke = 12, gap = 2 }) {
  const total = Object.values(data || {}).reduce((s, v) => s + (v || 0), 0);
  const radius = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  const palette = {
    paid: "stroke-emerald-500",
    partial: "stroke-amber-500",
    unpaid: "stroke-gray-300",
    overdue: "stroke-rose-500",
  };

  if (!total) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="shrink-0"
      >
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          className="fill-none stroke-slate-200"
          strokeWidth={stroke}
        />
      </svg>
    );
  }

  let offset = 0;
  const order = ["paid", "partial", "unpaid", "overdue"];

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
      aria-label="Invoice status donut"
    >
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        className="fill-none stroke-slate-100"
        strokeWidth={stroke}
      />
      {order.map((key) => {
        const val = data?.[key] || 0;
        if (!val) return null;
        const pct = val / total;
        const length = Math.max(0, circumference * pct - gap);
        const dasharray = `${length} ${circumference}`;
        const dashoffset = offset;
        offset -= circumference * pct;
        return (
          <circle
            key={key}
            cx={cx}
            cy={cy}
            r={radius}
            className={`fill-none ${palette[key] || "stroke-gray-300"}`}
            strokeWidth={stroke}
            strokeDasharray={dasharray}
            strokeDashoffset={dashoffset}
            transform={`rotate(-90 ${cx} ${cy})`}
            strokeLinecap="butt"
          />
        );
      })}
    </svg>
  );
}

/* ---------------------------- computation helpers ---------------------------- */

function computeInvoiceMetrics(invoices = []) {
  const now = Date.now();

  const buckets = { paid: 0, partial: 0, unpaid: 0, overdue: 0 };
  let arBalance = 0;
  let overdueCount = 0;
  let overdueAmount = 0;

  for (const inv of invoices) {
    const total = toNumber(inv.totalAmount ?? inv.total ?? 0);
    const paid = toNumber(inv.totalPaid ?? inv.paid ?? 0);
    const remaining = Math.max(0, total - paid);

    const rawStatus = String(inv.status || "").toLowerCase();
    const due = inv.dueDate ? new Date(inv.dueDate).getTime() : undefined;
    const isOverdue = remaining > 0 && due && due < now;

    const isWrittenOff =
      rawStatus === "written_off" ||
      rawStatus === "void" ||
      rawStatus === "voided";

    if (!isWrittenOff) {
      arBalance += remaining;

      if (remaining <= 0 || rawStatus === "paid") {
        buckets.paid += 1;
      } else if (isOverdue) {
        buckets.overdue += 1;
        overdueCount += 1;
        overdueAmount += remaining;
      } else if (paid > 0 && remaining > 0) {
        buckets.partial += 1;
      } else {
        buckets.unpaid += 1;
      }
    }
  }

  return {
    arBalance,
    overdueCount,
    overdueAmount,
    statusCounts: buckets,
  };
}

/**
 * Count of upcoming work in a window; null/undefined daysAhead = all future.
 */
function computeUpcomingWorkCount(works = [], daysAhead /* may be null */) {
  const now = Date.now();
  const end =
    daysAhead == null ? Number.POSITIVE_INFINITY : now + daysAhead * DAY_MS;

  let count = 0;
  for (const w of works) {
    const status = String(w.status || "").toLowerCase();
    if (CLOSED_STATUSES.has(status)) continue;

    const t = primaryWhen(w);
    if (!t) continue;
    const tt = new Date(t).getTime();
    if (tt >= now && tt <= end) count += 1;
  }
  return count;
}

/**
 * Pick the nearest future, open work item.
 */
function pickNextWork(works = []) {
  const now = Date.now();
  let best = null;
  let bestTs = Number.POSITIVE_INFINITY;

  for (const w of works) {
    const status = String(w.status || "").toLowerCase();
    if (CLOSED_STATUSES.has(status)) continue;

    const start = w?.schedule?.start || w?.dueDate || w?.schedule?.end;
    if (!start) continue;

    const ts = new Date(start).getTime();
    if (isNaN(ts) || ts < now) continue;

    if (ts < bestTs) {
      bestTs = ts;
      best = w;
    }
  }
  if (!best) return null;

  const title = best.title || best.name || best.subject || "Untitled";
  const start = best?.schedule?.start || best?.dueDate || null;
  const end = best?.schedule?.end || null;
  const tlabel = typeLabel(best?.type);

  return { title, start, end, type: tlabel };
}

function computeOpenQuotes(quotes = []) {
  const closed = new Set([
    "accepted",
    "approved",
    "declined",
    "cancelled",
    "expired",
    "converted",
  ]);
  let count = 0;
  let total = 0;

  for (const q of quotes) {
    const st = String(q.status || "").toLowerCase();
    if (closed.has(st)) continue;
    count += 1;
    total += toNumber(q.totalAmount ?? q.total ?? 0);
  }

  return { count, total };
}

/* --------------------------------- data io --------------------------------- */

async function fetchJSON(path, params) {
  const { data } = await api.get(path, { params });

  // Normalize a variety of common shapes
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.list)) return data.list;

  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data?.results)) return data.data.results;

  return [];
}

/* --------------------------------- main ---------------------------------- */
/**
 * Props:
 * - clientId?              // if provided, component fetches & computes KPIs unless autoLoad=false
 * - autoLoad=true
 * - currency
 * - kpis?: {
 *     arBalance,
 *     overdueCount, overdueAmount,
 *     workDueToday,                               // (preferred) OR tasksDueToday (back-compat)
 *     nextFollowUp, nextFollowUpLabel,
 *     quotesOpenCount?, quotesOpenAmount?,
 *     totalCollected?,
 *     // OPTIONAL (for controlled mode if you already know next work):
 *     nextWorkTitle?, nextWorkStart?, nextWorkEnd?, nextWorkType?
 *   }
 * - statusCounts?: { paid, partial, unpaid, overdue }
 * - onNavigate(tabId): 'invoices' | 'work' | 'quotes' | 'payments'
 * - className, kpiMinH, donutSize
 * - upcomingWindowDays=null  // null ⇒ all future (recommended). Set a number to window by days.
 */
export default function ClientSummaryStrip({
  clientId,
  autoLoad = true,
  currency,
  kpis,
  statusCounts,
  onNavigate = () => {},
  className = "",
  kpiMinH = 124,
  donutSize = 120,
  upcomingWindowDays = null, // <- default: ALL FUTURE
}) {
  // ✅ no USD hardcode fallback here either
  const cur = isValidCurrencyCode(currency) ? currency : null;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [computed, setComputed] = useState({
    arBalance: 0,
    overdueCount: 0,
    overdueAmount: 0,
    workUpcomingCount: 0,
    nextWork: null, // { title, start, end, type }
    quotesOpenCount: 0,
    quotesOpenAmount: 0,
    statusCounts: { paid: 0, partial: 0, unpaid: 0, overdue: 0 },
  });

  // Self-fetch mode (when clientId is supplied and kpis are not provided)
  useEffect(() => {
    let abort = false;
    async function run() {
      if (!clientId || !autoLoad || kpis) return;

      try {
        setLoading(true);
        setErr("");

        const [invoices, works, quotes] = await Promise.all([
          fetchJSON("/invoices", { clientId, limit: 1000 }),
          fetchJSON("/works", { clientId, limit: 1000 }),
          fetchJSON("/quotes", { clientId, limit: 1000 }),
        ]);
        if (abort) return;

        const inv = computeInvoiceMetrics(invoices);
        const upcomingCount = computeUpcomingWorkCount(
          works,
          upcomingWindowDays
        );
        const next = pickNextWork(works);
        const oq = computeOpenQuotes(quotes);

        setComputed({
          arBalance: inv.arBalance,
          overdueCount: inv.overdueCount,
          overdueAmount: inv.overdueAmount,
          workUpcomingCount: upcomingCount,
          nextWork: next,
          quotesOpenCount: oq.count,
          quotesOpenAmount: oq.total,
          statusCounts: inv.statusCounts,
        });
      } catch (e) {
        if (!abort) setErr(e?.message || "Failed to load client KPIs");
      } finally {
        if (!abort) setLoading(false);
      }
    }
    run();
    return () => {
      abort = true;
    };
  }, [clientId, autoLoad, kpis, upcomingWindowDays]);

  // Decide which data source to use (props vs computed)
  const k = useMemo(() => {
    if (kpis) {
      const workCount = kpis.workDueToday ?? kpis.tasksDueToday ?? 0;
      const nextWork =
        kpis.nextWorkTitle || kpis.nextWorkStart || kpis.nextWorkEnd
          ? {
              title: kpis.nextWorkTitle || "Untitled",
              start: kpis.nextWorkStart || null,
              end: kpis.nextWorkEnd || null,
              type: typeLabel(kpis.nextWorkType),
            }
          : null;

      return {
        arBalance: safeNum(kpis.arBalance),
        overdueCount: safeNum(kpis.overdueCount),
        overdueAmount: safeNum(kpis.overdueAmount),
        workUpcomingCount: safeNum(workCount),
        nextWork,
        quotesOpenCount: safeNum(kpis.quotesOpenCount),
        quotesOpenAmount: safeNum(kpis.quotesOpenAmount),
        nextFollowUpLabel: fmtMaybeDateLabel(
          kpis.nextFollowUp ?? kpis.nextFollowUpLabel
        ),
        totalCollected: safeNum(kpis.totalCollected),
      };
    }
    return {
      ...computed,
      nextFollowUpLabel: "—",
      totalCollected: 0,
    };
  }, [kpis, computed]);

  const donut = statusCounts || computed.statusCounts;

  const upcomingLabel = k.nextWork
    ? `Next ${k.nextWork.type?.toUpperCase?.() || "WORK"}`
    : upcomingWindowDays == null
    ? "Upcoming Work"
    : `Upcoming Work (${upcomingWindowDays}d)`;

  const upcomingValue = k.nextWork
    ? String(k.nextWork.title || "Untitled").toUpperCase()
    : safeNum(k.workUpcomingCount);

  const upcomingSub = k.nextWork
    ? fmtRange(k.nextWork.start, k.nextWork.end)
    : k.workUpcomingCount
    ? "Open items"
    : "No upcoming work";

  const tiles = [
    {
      key: "ar",
      label: "A/R Balance",
      value: nf(cur).format(safeNum(k.arBalance)),
      sub: "Unpaid + Partial",
      icon: CircleDollarSign,
      onClick: () => onNavigate("invoices"),
    },
    {
      key: "overdue",
      label: "Overdue",
      value: `${safeNum(k.overdueCount)} invoices`,
      sub: nf(cur).format(safeNum(k.overdueAmount)),
      icon: AlertTriangle,
      onClick: () => onNavigate("invoices"),
    },
    {
      key: "workUpcoming",
      label: upcomingLabel,
      value: upcomingValue,
      sub: upcomingSub,
      icon: CalendarDays,
      onClick: () => onNavigate("work"),
    },
    {
      key: "openQuotes",
      label: "Open Quotes",
      value: `${safeNum(k.quotesOpenCount)} quotes`,
      sub: nf(cur).format(safeNum(k.quotesOpenAmount)),
      icon: FileText,
      onClick: () => onNavigate("quotes"),
    },
    {
      key: "collected_opt",
      hidden: !k.totalCollected,
      label: "Total Collected",
      value: nf(cur).format(safeNum(k.totalCollected)),
      sub: "Lifetime",
      icon: PiggyBank,
      onClick: () => onNavigate("payments"),
    },
  ].filter((t) => !t.hidden);

  const donutLegend = useMemo(
    () => [
      { color: "bg-emerald-500", label: "Paid", count: safeNum(donut.paid) },
      {
        color: "bg-amber-500",
        label: "Partial",
        count: safeNum(donut.partial),
      },
      { color: "bg-gray-300", label: "Unpaid", count: safeNum(donut.unpaid) },
      { color: "bg-rose-500", label: "Overdue", count: safeNum(donut.overdue) },
    ],
    [donut]
  );

  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm ${className}`}
    >
      {/* loading / error strip */}
      {loading && (
        <div className="mb-3 h-1 w-full animate-pulse rounded bg-gradient-to-r from-transparent via-indigo-300 to-transparent" />
      )}
      {err && (
        <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-12">
        {/* LEFT: KPI tiles */}
        <div className="lg:col-span-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {tiles.map((t) => (
              <KpiCard
                key={t.key}
                label={t.label}
                value={t.value}
                sub={t.sub}
                icon={t.icon}
                minH={kpiMinH}
                onClick={t.onClick}
                aria-label={`${t.label} card`}
              />
            ))}
          </div>
        </div>

        {/* RIGHT: Donut + legend */}
        <div className="lg:col-span-4">
          <div
            className="flex h-full items-center justify-center rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            style={{ height: kpiMinH }}
          >
            <div className="flex w-full items-center justify-between gap-4">
              <div className="hidden w-28 shrink-0 text-xs font-semibold text-slate-800 md:block">
                Invoice Status
              </div>
              <MiniDonut data={donut || {}} size={donutSize} />
              <div className="min-w-[140px] space-y-1">
                {donutLegend.map((x) => (
                  <div
                    key={x.label}
                    className="flex items-center gap-2 text-[11px] leading-4 text-slate-600"
                  >
                    <span
                      className={`inline-block h-2.5 w-2.5 rounded-full ${x.color}`}
                    />
                    <span className="truncate">{x.label}</span>
                    <span className="text-slate-400">({x.count})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
